import React, { useState, useEffect, useRef } from 'react';
import { X, Save, Barcode, Camera } from 'lucide-react';
import BarcodeScanner from './BarcodeScanner';

const ProductModal = ({ isOpen, onClose, onSave, producto = null }) => {
  const [categorias, setCategorias] = useState([]);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const barcodeInputRef = useRef(null);
  const [formData, setFormData] = useState({
    sku: '',
    codigo_barras: '',
    nombre: '',
    descripcion: '',
    categoria_id: '',
    precio_compra: '',
    precio_venta: '',
    precio_mayoreo: '',
    stock_minimo: '',
    stock: '',
    unidad_medida: 'pza',
    activo: true,
  });

  useEffect(() => {
    if (isOpen) {
      loadCategorias();
      // Configurar listener para lectores USB de código de barras
      if (barcodeInputRef.current) {
        barcodeInputRef.current.focus();
      }
    }
  }, [isOpen]);

  const loadCategorias = async () => {
    try {
      const API_URL = (window.APP_CONFIG && window.APP_CONFIG.API_URL) || process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${API_URL}/categorias.php`);
      const data = await response.json();
      if (data.success) {
        setCategorias(data.data);
      }
    } catch (error) {
      console.error('Error al cargar categorías:', error);
    }
  };

  useEffect(() => {
    if (producto) {
      setFormData({
        sku: producto.sku || '',
        codigo_barras: producto.codigo_barras || '',
        nombre: producto.nombre || '',
        descripcion: producto.descripcion || '',
        categoria_id: producto.categoria_id || '',
        precio_compra: producto.precio_compra || '',
        precio_venta: producto.precio_venta || '',
        precio_mayoreo: producto.precio_mayoreo || '',
        stock_minimo: producto.stock_minimo || '',
        stock: producto.stock || '',
        unidad_medida: producto.unidad_medida || 'pza',
        activo: producto.activo !== undefined ? producto.activo : true,
      });
    } else {
      resetForm();
    }
  }, [producto, isOpen]);

  const resetForm = () => {
    setFormData({
      sku: '',
      codigo_barras: '',
      nombre: '',
      descripcion: '',
      categoria_id: '',
      precio_compra: '',
      precio_venta: '',
      precio_mayoreo: '',
      stock_minimo: '',
      stock: '',
      unidad_medida: 'pza',
      activo: true,
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validaciones básicas
    if (!formData.nombre || !formData.precio_venta) {
      alert('Por favor completa los campos obligatorios');
      return;
    }

    const normalized = {
      ...formData,
      precio_mayoreo: formData.precio_mayoreo === '' ? null : formData.precio_mayoreo,
    };

    onSave(normalized);
    resetForm();
  };

  const generarSKU = () => {
    const sku = 'SKU-' + Date.now().toString().substr(-8);
    setFormData({ ...formData, sku });
  };

  const generarCodigoBarras = () => {
    // Generar código EAN-13 simplificado (13 dígitos)
    const timestamp = Date.now().toString();
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const codigo = '77' + timestamp.substr(-9) + random; // 77 + 9 + 3 = 14 dígitos, tomamos 13
    const codigoFinal = codigo.substr(0, 13);
    setFormData({ ...formData, codigo_barras: codigoFinal });
  };

  const handleBarcodeScan = (code) => {
    setFormData({ ...formData, codigo_barras: code });
    setIsScannerOpen(false);
  };

  // Manejar entrada de lectores USB de código de barras
  const handleBarcodeInput = (e) => {
    const { value } = e.target;
    // Los lectores USB típicamente envían el código seguido de Enter
    if (value && value.length > 8) {
      // Asumimos que es un código válido si tiene más de 8 caracteres
      setFormData({ ...formData, codigo_barras: value });
    }
  };

  const handleBarcodeKeyPress = (e) => {
    // Si es Enter y hay contenido, es probable que sea de un lector USB
    if (e.key === 'Enter' && e.target.value) {
      e.preventDefault();
      // El código ya está en el input
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">
            {producto ? 'Editar Producto' : 'Nuevo Producto'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* SKU */}
            <div>
              <label className="label">SKU *</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="sku"
                  value={formData.sku}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="SKU del producto"
                  required
                />
                <button
                  type="button"
                  onClick={generarSKU}
                  className="btn-secondary whitespace-nowrap"
                >
                  Generar
                </button>
              </div>
            </div>

            {/* Código de barras */}
            <div>
              <label className="label">
                <Barcode className="inline w-4 h-4 mr-1" />
                Código de Barras
              </label>
              <div className="flex gap-2">
                <input
                  ref={barcodeInputRef}
                  type="text"
                  name="codigo_barras"
                  value={formData.codigo_barras}
                  onChange={(e) => {
                    handleChange(e);
                    handleBarcodeInput(e);
                  }}
                  onKeyPress={handleBarcodeKeyPress}
                  className="input-field"
                  placeholder="Código de barras"
                />
                <button
                  type="button"
                  onClick={() => setIsScannerOpen(true)}
                  className="btn-secondary whitespace-nowrap"
                  title="Escanear con cámara"
                >
                  <Camera className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={generarCodigoBarras}
                  className="btn-secondary whitespace-nowrap"
                  title="Generar código de barras automático"
                >
                  <Barcode className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Escanea con cámara, lector USB o genera un código automático
              </p>
            </div>

            {/* Nombre */}
            <div className="md:col-span-2">
              <label className="label">Nombre del Producto *</label>
              <input
                type="text"
                name="nombre"
                value={formData.nombre}
                onChange={handleChange}
                className="input-field"
                placeholder="Nombre del producto"
                required
              />
            </div>

            {/* Descripción */}
            <div className="md:col-span-2">
              <label className="label">Descripción</label>
              <textarea
                name="descripcion"
                value={formData.descripcion}
                onChange={handleChange}
                className="input-field"
                placeholder="Descripción del producto"
                rows="3"
              />
            </div>

            {/* Categoría */}
            <div>
              <label className="label">Categoría</label>
              <select
                name="categoria_id"
                value={formData.categoria_id}
                onChange={handleChange}
                className="input-field"
              >
                <option value="">Sin categoría</option>
                {categorias.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.nombre}
                  </option>
                ))}
              </select>
            </div>

            {/* Precio de compra */}
            <div>
              <label className="label">Precio de Compra</label>
              <input
                type="number"
                name="precio_compra"
                value={formData.precio_compra}
                onChange={handleChange}
                className="input-field"
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>

            {/* Precio de venta */}
            <div>
              <label className="label">Precio de Venta *</label>
              <input
                type="number"
                name="precio_venta"
                value={formData.precio_venta}
                onChange={handleChange}
                className="input-field"
                placeholder="0.00"
                step="0.01"
                min="0"
                required
              />
            </div>

            {/* Precio mayorista (opcional) */}
            <div>
              <label className="label">Precio Mayorista (opcional)</label>
              <input
                type="number"
                name="precio_mayoreo"
                value={formData.precio_mayoreo}
                onChange={handleChange}
                className="input-field"
                placeholder="(vacío = no aplica mayorista)"
                step="0.01"
                min="0"
              />
              <p className="text-xs text-gray-500 mt-1">
                Si lo dejas vacío, el producto no aparecerá en ventas mayorista.
              </p>
            </div>

            {/* Stock actual */}
            <div>
              <label className="label">Stock Actual</label>
              <input
                type="number"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
                className="input-field"
                placeholder="0"
                min="0"
              />
              <p className="text-xs text-gray-500 mt-1">
                Cantidad disponible en inventario
              </p>
            </div>

            {/* Stock mínimo */}
            <div>
              <label className="label">Stock Mínimo</label>
              <input
                type="number"
                name="stock_minimo"
                value={formData.stock_minimo}
                onChange={handleChange}
                className="input-field"
                placeholder="0"
                min="0"
              />
              <p className="text-xs text-gray-500 mt-1">
                Alerta cuando el stock esté por debajo de este número
              </p>
            </div>

            {/* Unidad de medida */}
            <div>
              <label className="label">Unidad de Medida</label>
              <select
                name="unidad_medida"
                value={formData.unidad_medida}
                onChange={handleChange}
                className="input-field"
              >
                <option value="pza">Pieza</option>
                <option value="kg">Kilogramo</option>
                <option value="lt">Litro</option>
                <option value="mt">Metro</option>
                <option value="caja">Caja</option>
                <option value="paquete">Paquete</option>
              </select>
            </div>

            {/* Estado activo */}
            <div className="flex items-center">
              <input
                type="checkbox"
                name="activo"
                checked={formData.activo}
                onChange={handleChange}
                className="mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label className="text-sm font-medium text-gray-700">
                Producto Activo
              </label>
            </div>
          </div>

          {/* Botones */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-primary flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {producto ? 'Actualizar' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>

      {/* Scanner de código de barras */}
      {isScannerOpen && (
        <BarcodeScanner
          isOpen={true}
          onDetected={handleBarcodeScan}
          onClose={() => setIsScannerOpen(false)}
        />
      )}
    </div>
  );
};

export default ProductModal;
